import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';
import { inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, of, switchMap, take } from 'rxjs';
import { BimplusWebsdkFeatures } from '../../interfaces/bimplus-websdk/interfaces';
import {
  TOKEN_VALIDATION,
  WithEmbeddedValidationFeature,
} from './with-embedded-validation.interface';
import { WithEmbeddedValidationHandlerService } from './services/with-embedded-validation-handler.service';
import { UrlFlagsService } from '../../../../services';
import { resolveEmbeddedToken } from 'utilities';

export function withEmbeddedValidation(): WithEmbeddedValidationFeature {
  return {
    kind: BimplusWebsdkFeatures.EmbeddedTokenValidation,
    providers: [
      {
        provide: TOKEN_VALIDATION,
        useFactory: () => {
          const urlFlagsService = inject(UrlFlagsService);
          const router = inject(Router);
          const route = inject(ActivatedRoute);
          const withEmbeddedValidation = new WithEmbeddedValidationHandlerService();

          if (!urlFlagsService.embedded()) {
            return withEmbeddedValidation.validationState;
          }

          router.events
            .pipe(
              filter((event): event is NavigationEnd => event instanceof NavigationEnd),
              take(1),
              switchMap(() => {
                const token = resolveEmbeddedToken(route);
                if (!token) {
                  const storedSessionToken = withEmbeddedValidation.getValidStoredSessionToken();
                  if (storedSessionToken) {
                    return of(storedSessionToken);
                  }
                  return withEmbeddedValidation.handleMissingToken();
                }
                // OCP-1464: reuse the existing session instead of re-running the
                // authentication flow when the same, still-valid token is provided.
                return withEmbeddedValidation.hasValidStoredSession(token)
                  ? of(token)
                  : withEmbeddedValidation.handleValidateToken(token);
              })
            )
            .subscribe({
              next: token =>
                token
                  ? withEmbeddedValidation.handleToken(token)
                  : withEmbeddedValidation.handleMissingToken(),
            });

          return withEmbeddedValidation.validationState;
        },
      },
    ],
    deps: [BIMPLUS_API],
  };
}
